import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import Talk from 'talkjs';
import { Session, Chatbox } from '@talkjs/react';
import { Doughnut } from 'react-chartjs-2';
import NavigationBar from '../components/NavigationBar'; // `NavigationBar` 컴포넌트 import
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../config/config';
import EducationalVideoDialog from '../components/EducationalVideoDialog';
import MedicalHistoryModal from '../components/MedicalHistoryModal';
import PatientTabs from '../components/PatientTabs';
import RiskBadge, { riskStatusKey } from '../components/RiskBadge';
import { NotebookPen, Video, Send, ChevronLeft, Stethoscope, Activity } from 'lucide-react';
import RecordsTab from './detail/RecordsTab';
import MemoSection from './detail/MemoSection';
import { calculateAge } from '../utils/age';
import { formatGender } from '../utils/gender';
import WeeklyInsightSection from '../components/WeeklyInsightSection';
import useWeeklyInsights from './detail/useWeeklyInsights';
import useFrail from '../utils/useFrail';
import useFrailBriefing from '../utils/useFrailBriefing';
import useFrailGuide from '../utils/useFrailGuide';
import FrailIndexSection from '../components/FrailIndexSection';
import '../css/DashboardDetail.css';

// 메모 카테고리. 세 곳의 select 와 두 곳의 유효성 검사가 같은 목록을 써야 한다 —
// 흩어져 있으면 하나만 고쳐서 "저장은 되는데 목록에서 안 보이는" 값이 생긴다.
const MEMO_CATEGORIES = ['운동', '식이', '간호', '기타'];

const getRiskLevel = (value, type) => {
    switch (type) {
        case 'BLOOD_GLUCOSE':
            if (value < 70) return { level: "위험 (저혈당)", color: "#000080" };
            if (value <= 100) return { level: "정상", color: "#2ECC40" };
            if (value <= 125) return { level: "경고 (고혈당 주의)", color: "#FFDC00" };
            if (value > 125) return { level: "위험 (고혈당)", color: "#FF4136" };
            return { level: "기록 없음", color: "#AAAAAA" };
        case 'BLOOD_GLUCOSE_MEAL':
            if (value < 90) return { level: "위험 (저혈당)", color: "#000080" };
            if (value <= 140) return { level: "정상", color: "#2ECC40" };
            if (value <= 200) return { level: "경고 (고혈당 주의)", color: "#FFDC00" };
            if (value > 200) return { level: "위험 (고혈당)", color: "#FF4136" };
            return { level: "기록 없음", color: "#AAAAAA" };
        case 'BP_SYSTOLIC':
            if (value < 90) return { level: "위험 (저혈압)", color: "#000080" };
            if (value <= 120) return { level: "정상", color: "#2ECC40" };
            if (value <= 140) return { level: "경고 (고혈압 주의)", color: "#FFDC00" };
            if (value > 140) return { level: "위험 (고혈압)", color: "#FF4136" };
            return { level: "기록 없음", color: "#AAAAAA" };
        case 'BP_DIASTOLIC':
            if (value < 60) return { level: "위험 (저혈압)", color: "#000080" };
            if (value <= 80) return { level: "정상", color: "#2ECC40" };
            if (value <= 90) return { level: "경고 (고혈압 주의)", color: "#FFDC00" };
            if (value > 90) return { level: "위험 (고혈압)", color: "#FF4136" };
            return { level: "기록 없음", color: "#AAAAAA" };
        case 'BMI':
            if (value < 18.5) return { level: "위험 (저체중)", color: "#000080" };
            if (value <= 23.0) return { level: "정상", color: "#2ECC40" };
            if (value <= 25.0) return { level: "경고 (과체중 주의)", color: "#FFDC00" };
            if (value > 25.0) return { level: "위험 (비만)", color: "#FF4136" };
            return { level: "기록 없음", color: "#AAAAAA" };
        default:
            return { level: "기록 없음", color: "#AAAAAA" };
    }
};

export default function DashboardDetail() {
    const { id } = useParams();
    const weeklyInsights = useWeeklyInsights(id);
    // 노쇠지수(스마트 프레일 지수). sdss-api 가 계산해 둔 최신 결과를 읽기만 한다.
    const frail = useFrail(id);
    const frailBriefing = useFrailBriefing(id, frail.data?.index?.id, frail.data?.briefingAvailable === true);
    const frailGuide = useFrailGuide(id, frail.data?.index?.id, frail.data?.guideAvailable === true);
    const location = useLocation();
    // 개요·측정기록·노쇠지수는 같은 컴포넌트가 그린다 — 데이터를 한 번만 받기 위해서다.
    // 주소 끝이 /records 면 측정기록, /frail 이면 노쇠지수, 아니면 개요. 끝 슬래시(/records/)도
    // 같은 탭으로 친다 — 그런 링크를 만들지는 않지만 주소를 직접 치는 사람이 개요로 떨어지지 않게.
    const detailTab = /\/records\/?$/.test(location.pathname) ? 'records'
        : /\/frail\/?$/.test(location.pathname) ? 'frail' : 'overview';
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [chartData, setChartData] = useState(null);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('BLOOD_GLUCOSE');
    const [selectedDate, setSelectedDate] = useState(null);
    const [memo, setMemo] = useState('');
    const [memoCategory, setMemoCategory] = useState('운동');
    const [memos, setMemos] = useState({});
    const [currentDate, setCurrentDate] = useState(new Date());
    const [showSendModal, setShowSendModal] = useState(false);
    // 이 대상자에게 보낸 문자 이력. 모달을 열 때만 받아 온다.
    const [smsHistory, setSmsHistory] = useState(null);
    const [smsHistoryError, setSmsHistoryError] = useState(null);
    const [showChat, setShowChat] = useState(false);
    const chatContainerRef = useRef(null);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showMemoModal, setShowMemoModal] = useState(false);
    const [showMemoTableModal, setShowMemoTableModal] = useState(false);
    const [memoTableData, setMemoTableData] = useState([]);
    const [memoTableMonth, setMemoTableMonth] = useState(new Date());
    const [memoTableLoading, setMemoTableLoading] = useState(false);
    const [isNewMemo, setIsNewMemo] = useState(false);
    const [editRowIdx, setEditRowIdx] = useState(null);
    const [editMemo, setEditMemo] = useState("");
    const [editCategory, setEditCategory] = useState("운동");
    const [editDate, setEditDate] = useState("");
    const [smsTitle, setSmsTitle] = useState('');
    const [smsType, setSmsType] = useState('공지');
    const [smsContent, setSmsContent] = useState('');
    const [carePlanShareUrl, setCarePlanShareUrl] = useState('');
    const [hasGuardianCarePlan, setHasGuardianCarePlan] = useState(false);
    const [selectedDateForTable, setSelectedDateForTable] = useState(null);
    const [allHealthData, setAllHealthData] = useState([]);

    // 병력 및 약물 입력 모달 상태
    const [showMedicalHistoryModal, setShowMedicalHistoryModal] = useState(false);
    // EMR 자동 요약(읽기 전용). 직원이 쓰는 medicalHistory/medication 과 별개로 들고 있는다.
    const [emrSummary, setEmrSummary] = useState({ history: '', medication: '', syncedAt: null });
    const [medicalHistory, setMedicalHistory] = useState('');
    const [medication, setMedication] = useState('');
    const [isOcrProcessing, setIsOcrProcessing] = useState(false);
    const [ocrTarget, setOcrTarget] = useState('history'); // 'history' or 'medication'
    const fileInputRef = useRef(null); // OCR 파일 입력을 위한 ref

    // 뒤로가기 핸들러 - Dashboard 상태 복원

    // ⚠️ 이름이 아니라 app_user_id 로 찾는다. message_history.recipient 는 이름
    //    문자열이라 동명이인을 구분하지 못하고(운영에 이름·전화번호가 모두 같은
    //    대상자가 있다), 여러 명에게 보낸 건은 이름이 콤마로 이어 붙어 있다.
    const loadSmsHistory = async () => {
        setSmsHistory(null);
        setSmsHistoryError(null);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/api/messages/history`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { appUserId: id }
            });
            setSmsHistory(response.data.history || []);
        } catch (error) {
            console.error('문자 발송 이력 조회 실패:', error.message);
            setSmsHistoryError('발송 이력을 불러오지 못했습니다.');
        }
    };

    const handleBackToDashboard = () => {
        const dashboardState = location.state?.dashboardState;
        if (dashboardState) {
            // Dashboard로 이동하면서 상태 복원
            navigate('/Dashboard', {
                state: {
                    restoreState: dashboardState
                }
            });
        } else {
            // 상태가 없으면 일반 뒤로가기
            window.history.back();
        }
    };

    // 알림톡 전송 핸들러
    const handleSendMessage = async (formData, selectedUsers) => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');

            const response = await axios.post(`${API_BASE_URL}/api/messages/alimtalk`, {
                users: selectedUsers.map(user => ({
                    user_id: user.user_id,
                    name: user.name,
                    mobile: user.mobile || '', // 전화번호 필드 추가
                    management_group: user.management_group
                })),
                messageType: "알림",
                messageTitle: "건강관리 알림",
                messageContent: formData.messageContent
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                alert('알림톡이 성공적으로 전송되었습니다.');
                setShowSendModal(false);
            } else {
                throw new Error(response.data.message || '알림톡 전송에 실패했습니다.');
            }

        } catch (error) {
            console.error('[알림톡 전송] 실패:', error);
            console.error('[알림톡 전송] 에러 상세:', error.response?.data);
            alert(error.response?.data?.message || '알림톡 전송 중 오류가 발생했습니다.');
        } finally {
            console.log('[알림톡 전송] 완료');
            setIsLoading(false);
        }
    };

    const dateFormatter = (date) => {
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
        const d = new Date(date);
        const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
        return kst.toISOString().split('T')[0];
    };

    // 데이터 조회 함수 수정
    const fetchMemos = async () => {
        console.log('fetchMemos - user:', user);
        console.log('fetchMemos - user.user_id:', user?.user_id);
        if (!user?.user_id || user.user_id === '-') {
            setMemos({});
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const year = currentDate.getFullYear();
            const month = currentDate.getMonth() + 1;

            const response = await axios.get(`/api/memos/${user?.user_id}/${year}/${month}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                const memoData = response.data.data.reduce((acc, memo) => {
                    const dateStr = memo.memo_data;
                    if (!acc[dateStr]) {
                        acc[dateStr] = {
                            memos: [],
                            hasData: true
                        };
                    }
                    acc[dateStr].memos.push({
                        content: memo.content,
                        category: memo.category,
                        id: memo.id
                    });
                    return acc;
                }, {});
                setMemos(memoData);
            }
        } catch (error) {
            console.error('메모 조회 실패:', error);
            alert('메모 조회 중 오류가 발생했습니다');
        }
    };

    // 월 변경 시 데이터 다시 조회
    useEffect(() => {
        if (user?.user_id) {
            fetchMemos();
        }
    }, [currentDate, user?.user_id]);

    // 월 이동 버튼 핸들러
    const handlePrevMonth = () => {
        const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
        setCurrentDate(newDate);
        setSelectedDate(`${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}-01`);
    };

    const handleNextMonth = () => {
        const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1);
        setCurrentDate(newDate);
        setSelectedDate(`${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}-01`);
    };

    // currentDate가 바뀔 때마다 selectedDate를 1일로 자동 설정
    useEffect(() => {
        setSelectedDate(`${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-01`);
    }, [currentDate]);

    // TalkJS User 동기화
    const syncUser = useCallback(() => {
        const adminId = localStorage.getItem('id') || 'admin';
        const adminName = localStorage.getItem('user_name') || '관리자';
        return new Talk.User({
            id: adminId,
            name: adminName,
            email: 'admin@example.com',
            photoUrl: 'https://talkjs.com/images/avatar-1.jpg',
            welcomeMessage: '안녕하세요! 문의하실 내용을 남겨주세요.',
            role: 'default',
        });
    }, []);

    // TalkJS Conversation 동기화
    const syncConversation = useCallback((session) => {
        if (!user || !user.user_id || user.user_id === '-') return null;

        const other = new Talk.User({
            id: user.user_id,
            name: user.name,
            email: 'patient@example.com',
            photoUrl: 'https://talkjs.com/images/avatar-2.jpg',
            welcomeMessage: '안녕하세요.',
            role: 'default',
        });

        const conversationId = `room_${user.user_id}`;
        const conversation = session.getOrCreateConversation(conversationId);
        conversation.setParticipant(session.me);
        conversation.setParticipant(other);
        return conversation;
    }, [user]);

    useEffect(() => {
        const fetchUserData = async () => {
            setLoading(true);
            setError(null);
            try {
                const token = localStorage.getItem('token');

                // 슈퍼 관리자 권한 확인
                const adminCheckResponse = await axios.get(`${API_BASE_URL}/api/check-admin-email`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                const isSuperAdmin = adminCheckResponse.data?.isSuperAdmin || false;

                // 슈퍼 관리자인 경우 다른 엔드포인트 사용
                const endpoint = isSuperAdmin
                    ? `${API_BASE_URL}/api/health-data/${id}/super-admin`
                    : `${API_BASE_URL}/api/health-data/${id}`;

                const response = await axios.get(endpoint, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                await new Promise(resolve => setTimeout(resolve, 100));

                if (response.data && response.data.success) {
                    const healthData = response.data.data || [];

                    if (healthData.length === 0) {
                        setUser({
                            id: id,
                            user_id: id,
                            name: '미등록 사용자',
                            has_smart_ring: false,
                            birthdate: '-',
                            gender: '-',
                            management_group: '-',
                            focuspatient: false,
                            bloodGlucose: '-',
                            bloodPressure: {
                                systolic: '-',
                                diastolic: '-'
                            },
                            bmi: '-',
                            measureDate: '-',
                            last_modified_date: '-',
                            mobile: '-',
                            isSuperAdmin: isSuperAdmin
                        });
                        setAllHealthData([]);
                        setChartData([]);
                        return;
                    }

                    setAllHealthData(healthData);

                    const groupedData = healthData.reduce((acc, record) => {
                        if (record.measure_type === 'BLOOD_GLUCOSE') {
                            acc.bloodGlucose = record;
                        } else if (record.measure_type === 'BP_SYSTOLIC') {
                            acc.systolic = record;
                        } else if (record.measure_type === 'BP_DIASTOLIC') {
                            acc.diastolic = record;
                        } else if (record.measure_type === 'BMI') {
                            acc.bmi = record;
                        } else if (record.measure_type === 'BLOOD_GLUCOSE_MEAL') {
                            acc.bloodGlucose_meal = record;
                        }
                        return acc;
                    }, {});

                    const latestRecord = healthData[0];
                    const latestSystolic = groupedData.systolic;
                    const latestDiastolic = groupedData.diastolic;

                    // ⚠️ 여기에 위험도를 미리 계산해 user 에 담지 말 것.
                    //    화면의 배지는 allHealthData 에서 기록 존재를 확인한 뒤 만든다(아래 metricCards).
                    //    user 에 담아 두면 값이 없는 대상자에게 getRiskLevel(없는 값 ?? 0, …) 꼴이
                    //    되어 0 이 의미 있는 구간으로 떨어진다 — 70d32cf 가 고친 것이 그 모양이다.

                    // 측정 시작일시/최종측정일시 계산. 둘 다 개요가 읽는다 —
                    // 시작일은 정보 카드가, 최종일은 "마지막 측정" 줄이 쓴다.
                    // ⚠️ 측정이 하나도 없는 대상자는 measure_date 가 null 인 행 하나로 온다.
                    //    그대로 reduce 하면 new Date(null) 이 1970-01-01 이 되어 그 날짜가 찍힌다.
                    //    날짜가 있는 행만 골라 계산하고, 없으면 null 로 두어 화면이 안내를 그리게 한다.
                    let minMeasureDate = null;
                    let maxMeasureDate = null;
                    const dated = healthData.filter((record) => record.measure_date);
                    if (dated.length > 0) {
                        minMeasureDate = dated.reduce((min, cur) => (
                            new Date(cur.measure_date) < new Date(min.measure_date) ? cur : min
                        )).measure_date;
                        maxMeasureDate = dated.reduce((max, cur) => (
                            new Date(cur.measure_date) > new Date(max.measure_date) ? cur : max
                        )).measure_date;
                    }

                    setUser({
                        id: latestRecord.id,
                        user_id: latestRecord.user_id,
                        name: latestRecord.name,
                        // 스마트링 착용 여부. 이 값이 참일 때만 바이탈 버튼이 보인다.
                        has_smart_ring: latestRecord.has_smart_ring === true,
                        birthdate: latestRecord.birthdate,
                        gender: latestRecord.gender,
                        management_group: latestRecord.management_group,
                        focuspatient: latestRecord.focuspatient,
                        bloodGlucose: groupedData.bloodGlucose?.current_value || '-',
                        bloodPressure: {
                            systolic: latestSystolic?.current_value || '-',
                            diastolic: latestDiastolic?.current_value || '-'
                        },
                        bmi: groupedData.bmi?.current_value || '-',
                        measureDate: latestRecord.measure_date,
                        last_modified_date: latestRecord.last_modified_date,
                        mobile: latestRecord.mobile,
                        measureStartDate: minMeasureDate,
                        measureEndDate: maxMeasureDate,
                        isSuperAdmin: isSuperAdmin
                    });

                    // Filter data based on activeTab
                    const filteredData = healthData.filter(item => {
                        if (activeTab === 'BLOOD_GLUCOSE') {
                            return item.measure_type === 'BLOOD_GLUCOSE';
                        } else if (activeTab === 'BLOOD_GLUCOSE_MEAL') {
                            return item.measure_type === 'BLOOD_GLUCOSE_MEAL';
                        } else if (activeTab === 'BP_SYSTOLIC') {
                            return item.measure_type === 'BP_SYSTOLIC' || item.measure_type === 'BP_DIASTOLIC';
                        } else if (activeTab === 'BMI') {
                            return item.measure_type === 'BMI';
                        }
                        return false;
                    });

                    setChartData(filteredData);

                } else {
                    throw new Error(response.data?.message || '데이터를 불러오는데 실패했습니다.');
                }

            } catch (error) {
                if (error.response && error.response.status === 401) {
                    alert('인증이 만료되었습니다. 다시 로그인해주세요.');
                    localStorage.removeItem('token');
                    navigate('/');
                } else {
                    console.error('Error fetching health data:', error);
                    setError(error.message || '사용자 건강 데이터를 불러오는 중 오류가 발생했습니다.');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
    }, [id, navigate, activeTab]);

    // 메모 테이블용 데이터 조회 함수
    const fetchMemoTableData = async (targetDate) => {
        if (!user?.user_id) return;
        setMemoTableLoading(true);
        try {
            const token = localStorage.getItem('token');
            const year = targetDate.getFullYear();
            const month = targetDate.getMonth() + 1;
            const response = await axios.get(`/api/memos/${user.user_id}/${year}/${month}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setMemoTableData(response.data.data);
            } else {
                setMemoTableData([]);
            }
        } catch (error) {
            setMemoTableData([]);
        } finally {
            setMemoTableLoading(false);
        }
    };

    // 메모 테이블 모달 열기
    const handleOpenMemoTable = () => {
        setShowMemoTableModal(true);
        const baseDate = new Date();
        setMemoTableMonth(baseDate);
        setSelectedDateForTable(null);
        fetchMemoTableData(baseDate);
    };
    // 월 이동
    const handleMemoTablePrevMonth = () => {
        const prev = new Date(memoTableMonth.getFullYear(), memoTableMonth.getMonth() - 1);
        setMemoTableMonth(prev);
        setSelectedDate(`${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}-01`);
        fetchMemoTableData(prev);
    };
    const handleMemoTableNextMonth = () => {
        const next = new Date(memoTableMonth.getFullYear(), memoTableMonth.getMonth() + 1);
        setMemoTableMonth(next);
        setSelectedDate(`${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-01`);
        fetchMemoTableData(next);
    };
    // 메모 삭제(작업)
    // (API에 삭제 엔드포인트가 없으므로, 삭제 버튼은 UI만 표시)

    // memoTableMonth가 바뀔 때마다 하단 입력값 초기화
    useEffect(() => {
        setSelectedDate(`${memoTableMonth.getFullYear()}-${String(memoTableMonth.getMonth() + 1).padStart(2, '0')}-01`);
        setMemo('');
        setMemoCategory('운동');
    }, [memoTableMonth]);

    // 캘린더에서 메모가 여러 건인 날짜를 클릭했을 때 "월별 메모 내역" 테이블을 연다.
    // MemoSection 으로 옮긴 handleCalendarDayClick 이 이 콜백을 호출한다 — 테이블
    // 모달 상태(showMemoTableModal 등)는 이 모달과 공유돼 있어 그대로 여기 둔다.
    const handleOpenMemoTableForDate = (dateStr) => {
        setShowMemoTableModal(true);
        setMemoTableMonth(new Date(dateStr));
        setSelectedDateForTable(dateStr);
        fetchMemoTableData(new Date(dateStr));
    };

    const getDateOnly = (dateStr) => {
        if (!dateStr) return '';
        return dateStr.split('T')[0];
    };

    const filteredMemoTableData = selectedDateForTable
        ? memoTableData.filter(row => getDateOnly(row.memo_data) === getDateOnly(selectedDateForTable))
        : memoTableData;

    if (loading) {
        return (
            <div className="loading-container">
                로딩 중...
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <div>⚠️ 오류가 발생했습니다</div>
                <div>{error}</div>
                <button
                    className="error-button"
                    onClick={() => window.location.reload()}
                >
                    새로고침
                </button>
            </div>
        );
    }

    // 메모 저장
    const handleSaveMemo = async () => {
        console.log('user:', user);
        console.log('user.user_id:', user?.user_id);
        console.log('selectedDate:', selectedDate);
        console.log('memo:', memo);
        console.log('memoCategory:', memoCategory);
        const created_id = localStorage.getItem('id');
        console.log('created_id:', created_id);
        const created_at = new Date().toISOString();
        console.log('created_at:', created_at);
        console.log('API 요청 payload:', {
            management_id: user?.user_id,
            memo_data: selectedDate,
            content: memo,
            category: memoCategory,
            created_id: created_id,
            created_at: created_at
        });
        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/memos', {
                management_id: user?.user_id,
                memo_data: selectedDate,
                content: memo,
                category: memoCategory,
                created_id: created_id,
                created_at: created_at
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // 메모 저장 후 상태 업데이트
            setMemos(prev => ({
                ...prev,
                [selectedDate]: {
                    memos: [...(prev[selectedDate]?.memos || []), {
                        content: memo,
                        category: memoCategory
                    }],
                    hasData: true
                }
            }));

            setShowMemoModal(false);
            setMemo('');
            setMemoCategory('운동');
            fetchMemoTableData(memoTableMonth);
            // === input 초기화 ===
            setMemo('');
            setMemoCategory('운동');
            setSelectedDate(`${memoTableMonth.getFullYear()}-${String(memoTableMonth.getMonth() + 1).padStart(2, '0')}-01`);
        } catch (error) {
            console.error('메모 저장 실패:', error);
            alert('메모 저장 중 오류가 발생했습니다');
        }
    };

    // 메모 삭제
    const handleDeleteMemo = async (memoId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/memos/${memoId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // 메모 삭제 후 상태 업데이트
            setMemos(prev => {
                const newMemos = { ...prev };
                if (newMemos[selectedDate]) {
                    newMemos[selectedDate].memos = newMemos[selectedDate].memos.filter(m => m.id !== memoId);
                    if (newMemos[selectedDate].memos.length === 0) {
                        newMemos[selectedDate].hasData = false;
                    }
                }
                return newMemos;
            });

            setShowMemoModal(false);
            setMemo('');
            setMemoCategory('운동');
            fetchMemos();
        } catch (error) {
            alert('메모 삭제 중 오류가 발생했습니다');
        }
    };

    // 메모 수정 저장
    const handleUpdateMemo = async (row, idx) => {
        try {
            const token = localStorage.getItem('token');
            const created_id = localStorage.getItem('id');
            // 기존 메모 삭제 후 새로 저장 (덮어쓰기)
            await axios.delete(`/api/memos/${user?.user_id}/${row.memo_data}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            await axios.post('/api/memos', {
                management_id: user?.user_id,
                memo_data: editDate,
                content: editMemo,
                category: editCategory,
                created_id: created_id,
                created_at: new Date().toISOString()
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setEditRowIdx(null);
            fetchMemoTableData(memoTableMonth);
        } catch (error) {
            alert('메모 수정 중 오류가 발생했습니다');
        }
    };

    // 메모 삭제
    const handleDeleteMemoRow = async (row) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/memos/${row.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchMemoTableData(memoTableMonth);
        } catch (error) {
            alert('메모 삭제 중 오류가 발생했습니다');
        }
    };

    // 문자 전송 모달 오픈 핸들러
    const handleOpenSendModal = async () => {
        setSmsTitle(`[${user?.management_group || '-'}]\n`);
        setCarePlanShareUrl('');
        setHasGuardianCarePlan(false);
        setShowSendModal(true);
        loadSmsHistory();

        // 보호자용 케어플랜이 있는 사용자에게만 링크 버튼을 보여준다.
        if (!user?.user_id || user.user_id === '-') return;
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE_URL}/api/careplan/${user.user_id}/latest-plan`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setHasGuardianCarePlan(Boolean(response.data?.data?.guardian_content));
        } catch (error) {
            console.error('케어플랜 존재 여부 확인 중 오류 발생:', error);
            setHasGuardianCarePlan(false);
        }
    };

    // SMS 발송 함수
    const handleSendSMS = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const messageData = {
                message_type: smsType,
                title: smsTitle,
                content: smsContent,
                sender: localStorage.getItem('user_name') || '관리자',
                recipient: user.name,
                app_user_id: user.user_id, // 병원 연락처 꼬리말 판정에 쓰인다
                created_id: parseInt(localStorage.getItem('id')),
                created_at: new Date().toISOString(),
                scheduled_date: null,
                sent_date: new Date().toISOString(),
                messages: [{
                    to: user.mobile,
                    content: smsContent
                }]
            };
            await axios.post('/api/messages/send-direct', messageData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert('문자가 성공적으로 발송되었습니다.');
            setShowSendModal(false);
            setSmsTitle('');
            setSmsType('SMS');
            setSmsContent('');
            setCarePlanShareUrl('');
        } catch (error) {
            console.error('문자 발송 중 오류 발생:', error);
            alert('문자 발송 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    // 케어플랜 본문은 문자 한 통에 들어가지 않아서 링크만 넣는다.
    const insertCarePlanLink = async () => {
        if (!user?.user_id || user.user_id === '-') {
            alert('사용자 정보를 확인할 수 없습니다.');
            return;
        }

        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API_BASE_URL}/api/careplan/${user.user_id}/share-link`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const url = response.data?.url;
            if (!url) throw new Error('링크가 비어 있습니다.');

            setCarePlanShareUrl(url);
            setSmsContent(prev => (prev.includes(url)
                ? prev
                : `${prev.replace(/\s+$/, '')}\n▶ 보호자용 건강관리 요약본: ${url}`.trim()));
        } catch (error) {
            console.error('케어플랜 링크 발급 중 오류 발생:', error);
            alert('케어플랜 링크를 만들지 못했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    // 만료가 없는 링크라 회수 수단이 화면에 있어야 한다.
    const revokeCarePlanLink = async () => {
        if (!carePlanShareUrl) return;
        if (!window.confirm('이 링크를 폐기하시겠습니까?\n이미 문자로 받은 보호자도 더 이상 열 수 없습니다.')) return;

        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const shareToken = carePlanShareUrl.split('/').pop();
            await axios.post(
                `${API_BASE_URL}/api/careplan/share-links/${shareToken}/revoke`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSmsContent(prev => prev.replace(`\n▶ 보호자용 건강관리 요약본: ${carePlanShareUrl}`, ''));
            setCarePlanShareUrl('');
            alert('링크를 폐기했습니다.');
        } catch (error) {
            console.error('케어플랜 링크 폐기 중 오류 발생:', error);
            alert('링크를 폐기하지 못했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    // 1:1 채팅 URL 문자 발송 함수
    const sendChatUrlSMS = async (showConfirm = false) => {
        if (!user || !user.mobile || user.mobile === '-') {
            alert('사용자의 유효한 전화번호가 없습니다.');
            return;
        }

        if (showConfirm) {
            const isConfirmed = window.confirm('사용자에게 1:1 채팅방 접속 URL 문자를 전송하시겠습니까?');
            if (!isConfirmed) return;
        }

        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const chatUrl = `${window.location.origin}/chat?room=room_${user.user_id}`;
            const title = `[${user.management_group || '건강관리센터'}]\n`;
            const content = `${title}안녕하세요, ${user.name}님.\n건강 상담을 위한 1:1 채팅방이 개설되었습니다.\n아래 링크를 클릭하여 채팅에 참여해주세요.\n\n▶ 채팅방 입장하기: ${chatUrl}`;

            const messageData = {
                message_type: '알림',
                title: '채팅방 개설 안내',
                content: content,
                sender: localStorage.getItem('user_name') || '관리자',
                recipient: user.name,
                app_user_id: user.user_id, // 병원 연락처 꼬리말 판정에 쓰인다
                created_id: parseInt(localStorage.getItem('id')),
                created_at: new Date().toISOString(),
                scheduled_date: null,
                sent_date: new Date().toISOString(),
                messages: [{
                    to: user.mobile,
                    content: content
                }]
            };

            await axios.post('/api/messages/send-direct', messageData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert('채팅 URL 문자가 성공적으로 발송되었습니다.');
        } catch (error) {
            console.error('채팅 URL 문자 발송 중 오류 발생:', error);
            alert('채팅 URL 문자 발송 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggleChat = async () => {
        const nextShowChat = !showChat;
        setShowChat(nextShowChat);
        if (nextShowChat) {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`/api/messages/chat-link-history`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { recipientName: user.name }
                });
                if (response.data && response.data.success && response.data.count === 0) {
                    setTimeout(() => {
                        sendChatUrlSMS(true);
                    }, 200);
                }
            } catch (error) {
                console.error('채팅 URL 발송 이력 조회 중 오류 발생:', error);
            }
        }
    };

    // 병력 및 약물 데이터 가져오기
    const fetchMedicalHistory = async () => {
        if (!user?.user_id || user.user_id === '-') return;
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`/api/medical-history/${user.user_id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success && response.data.data) {
                const d = response.data.data;
                setMedicalHistory(d.history_content || '');
                setMedication(d.medication_content || '');
                // EMR 이 보내온 진료 데이터로 서버가 만든 요약. 읽기 전용이다 —
                // 다음 수신 때 다시 만들어지므로 여기서 고쳐도 남지 않는다.
                setEmrSummary({
                    history: d.emr_history_content || '',
                    medication: d.emr_medication_content || '',
                    syncedAt: d.emr_synced_at || null
                });
            } else {
                setMedicalHistory('');
                setMedication('');
                setEmrSummary({ history: '', medication: '', syncedAt: null });
            }
        } catch (error) {
            console.error('병력 데이터 조회 오류:', error);
        }
    };

    // 모달 열 때 데이터 가져오기
    const handleOpenMedicalHistory = () => {
        setShowMedicalHistoryModal(true);
        fetchMedicalHistory();
    };

    // 병력 및 약물 데이터 저장
    const handleSaveMedicalHistory = async () => {
        if (!user?.user_id || user.user_id === '-') return;
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`/api/medical-history/${user.user_id}`, {
                history_content: medicalHistory,
                medication_content: medication
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                alert('병력 및 약물 정보가 저장되었습니다.');
                setShowMedicalHistoryModal(false);
            }
        } catch (error) {
            console.error('병력 데이터 저장 오류:', error);
            alert('저장 중 오류가 발생했습니다.');
        }
    };

    // OCR 캡쳐 핸들러
    const handleOcrClick = (target) => {
        setOcrTarget(target);
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setIsOcrProcessing(true);
        try {
            const result = await window.Tesseract.recognize(file, 'kor', {
                logger: m => console.log(m)
            });
            
            const text = result.data.text;
            if (ocrTarget === 'history') {
                setMedicalHistory(prev => prev + (prev ? '\n' : '') + text);
            } else {
                setMedication(prev => prev + (prev ? '\n' : '') + text);
            }
        } catch (error) {
            console.error('OCR 처리 오류:', error);
            alert('이미지 인식 중 오류가 발생했습니다.');
        } finally {
            setIsOcrProcessing(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    // 생년월일과 나이는 헤더 신원 줄에 함께 적는다. 여기서 생년월일의 쓸모는 임상이
    // 아니라 식별이다 — 운영에 이름과 전화번호가 모두 같은 대상자가 있다(위 loadSmsHistory
    // 주석 참고). 그때 둘을 가르는 것이 생년월일이다.
    //
    // 둘은 같은 사실(생년월일이 있는가)에서 나오므로 함께 나오고 함께 사라진다. 하나는
    // '-' 를 그리고 다른 하나는 통째로 빠지면, 같은 값을 두고 화면이 두 말을 하는 셈이다.
    // 만 나이다 — 케어플랜이 AI 에 넘기는 것과 같은 계산을 쓴다.
    const hasBirthdate = user.birthdate && user.birthdate !== '-';
    const age = calculateAge(user.birthdate);
    const identity = hasBirthdate
        ? `${dateFormatter(user.birthdate)}${age != null ? ` (${age}세)` : ''} · ${formatGender(user.gender)}`
        : formatGender(user.gender);

    // 전화번호는 이 흐름에서 여기 말고는 볼 곳이 없다 — 대시보드 목록에도 열이 없다.
    // 표기는 user.mobile 그대로 보여 주고 href 에서만 숫자를 뽑는다(하이픈이 있든 없든 걸린다).
    const hasMobile = user.mobile && user.mobile !== '-';
    const telHref = hasMobile ? `tel:${String(user.mobile).replace(/[^0-9+]/g, '')}` : null;

    // 측정이 하나도 없는 대상자에게는 담기지 않는다(위 fetch 참고).
    const measureStartText = user.measureStartDate && user.measureStartDate !== '-'
        ? dateFormatter(user.measureStartDate)
        : '기록 없음';

    // "이번 주 요약" 자리에 쓸 사실 두 가지. 새 API 없이 이미 받은 allHealthData 에서 센다.
    // - latestDate: 이미 계산된 user.measureEndDate 를 그대로 옮겨 쓴다(중복 계산 금지).
    // - measuredDays: 최근 7일(오늘 포함) 중 서로 다른 측정일이 며칠인지.
    const latestDate = user.measureEndDate && user.measureEndDate !== '-'
        ? dateFormatter(user.measureEndDate)
        : null;
    // latestDate 와 같은 조건을 쓴다 — measure_date 가 없는 행만 있으면(예: 값 없이
    // 자리만 잡힌 행) "최근 측정 기록이 없습니다" 와 "최근 7일 중 0일" 이 나란히 겹친다.
    const measuredDays = allHealthData.some((r) => r.measure_date)
        ? (() => {
            const todayStr = dateFormatter(new Date());
            const cutoff = new Date(todayStr);
            cutoff.setDate(cutoff.getDate() - 6);
            const cutoffStr = cutoff.toISOString().split('T')[0];
            return new Set(
                allHealthData
                    .filter((r) => r.measure_date)
                    .map((r) => dateFormatter(r.measure_date))
                    .filter((d) => d >= cutoffStr && d <= todayStr)
            ).size;
        })()
        : null;

    // 지표 카드 네 장. allHealthData(= GET /api/health-data/:id 응답)에서 직접 만든다.
    // user 객체를 쓰지 않는 이유: before_value 가 없고, 식후 혈당이 담겨 있지 않다.
    const latestOf = (type) => (allHealthData || [])
        .filter((r) => r.measure_type === type && r.current_value != null && r.current_value !== '')
        .sort((a, b) => String(b.measure_date || '').localeCompare(String(a.measure_date || '')))[0] || null;

    const delta = (record) => (record?.before_value == null || record.before_value === ''
        ? '직전 기록 없음'
        : `직전 ${record.before_value} → 현재 ${record.current_value}`);

    // 카드의 숫자가 언제 값인지. 30일이 지난 값은 위험·경고 색을 빼고 "오래된 값" 으로 격하한다 —
    // 6개월 전 식후혈당 205 가 빨간 배지를 달고 있던 운영 사례(2026-09-29 사용자). 주간 인사이트의 "직전 4주" 와 같은 기준.
    const STALE_DAYS = 30;
    const measuredOn = (record) => (record?.measure_date ? dateFormatter(record.measure_date) : null);
    const daysAgo = (ymd) => (ymd ? Math.floor((Date.now() - new Date(`${ymd}T00:00:00+09:00`).getTime()) / 86400000) : null);
    const isStale = (record) => { const d = daysAgo(measuredOn(record)); return d != null && d > STALE_DAYS; };
    const shortDate = (ymd) => (ymd ? `${Number(ymd.slice(5, 7))}.${Number(ymd.slice(8, 10))}` : '');
    const withDate = (text, record) => (measuredOn(record) ? `${text} · ${shortDate(measuredOn(record))} 측정` : text);

    // 순위가 높을수록 나쁜 단계. 혈압 카드의 배지를 고를 때만 쓴다.
    const SEVERITY = { danger: 3, 'danger-low': 3, caution: 2, normal: 1, none: 0 };
    const worse = (a, b) => (SEVERITY[riskStatusKey(a)] >= SEVERITY[riskStatusKey(b)] ? a : b);

    const sys = latestOf('BP_SYSTOLIC');
    const dia = latestOf('BP_DIASTOLIC');
    const glu = latestOf('BLOOD_GLUCOSE');
    const gluMeal = latestOf('BLOOD_GLUCOSE_MEAL');
    const bmi = latestOf('BMI');

    const metricCards = [
        (sys || dia) && {
            key: 'BP', title: '혈압', unit: 'mmHg',
            value: `${sys?.current_value ?? '-'} / ${dia?.current_value ?? '-'}`,
            // 한쪽만 기록된 경우가 있다. 없는 쪽을 0 으로 채우면 임계표의 가장 낮은 가지인
            // 저혈압 위험으로 떨어져, 기록에 없는 경보가 뜬다. 있는 쪽만 견준다.
            level: [
                sys && getRiskLevel(sys.current_value, 'BP_SYSTOLIC').level,
                dia && getRiskLevel(dia.current_value, 'BP_DIASTOLIC').level,
            ].filter(Boolean).reduce(worse),
            // 증감도 같은 이유로 있는 쪽을 본다. 늘 sys 만 읽으면 이완기만 기록된 경우
            // 실제 직전 값이 있어도 "직전 기록 없음"이 뜬다.
            delta: withDate(delta(sys || dia), sys || dia),
            measuredOn: measuredOn(sys || dia), stale: isStale(sys || dia),
        },
        glu && {
            key: 'BLOOD_GLUCOSE', title: '식전 혈당', unit: 'mg/dL',
            value: glu.current_value,
            level: getRiskLevel(glu.current_value, 'BLOOD_GLUCOSE').level,
            delta: withDate(delta(glu), glu),
            measuredOn: measuredOn(glu), stale: isStale(glu),
        },
        gluMeal && {
            key: 'BLOOD_GLUCOSE_MEAL', title: '식후 혈당', unit: 'mg/dL',
            value: gluMeal.current_value,
            level: getRiskLevel(gluMeal.current_value, 'BLOOD_GLUCOSE_MEAL').level,
            delta: withDate(delta(gluMeal), gluMeal),
            measuredOn: measuredOn(gluMeal), stale: isStale(gluMeal),
        },
        bmi && {
            key: 'BMI', title: '체질량지수', unit: '',
            value: Number(bmi.current_value).toFixed(1),
            level: getRiskLevel(bmi.current_value, 'BMI').level,
            delta: withDate(delta(bmi), bmi),
            measuredOn: measuredOn(bmi), stale: isStale(bmi),
        },
    ].filter(Boolean);

    // 요약 카드에 붙일 한 줄: 30일 넘게 안 잰 지표. 담당자가 "다시 재 보시라" 고 안내할 근거.
    const staleNote = (() => {
        const stale = metricCards.filter((c) => c.stale);
        if (stale.length === 0) return null;
        const longDate = (ymd) => `${Number(ymd.slice(5, 7))}월 ${Number(ymd.slice(8, 10))}일`;
        return `${stale.map((c) => `${c.title}은 ${longDate(c.measuredOn)} 이후 기록이 없어요`).join('. ')}.`;
    })();

    return (
        <div className="dashboard-detail-container">
            <NavigationBar />
            <div className="sds-page detail-page">
                <div className="detail-page__crumb">
                    <button className="sds-btn sds-btn--ghost sds-btn--sm" onClick={handleBackToDashboard}>
                        <ChevronLeft size={16} aria-hidden="true" />대시보드
                    </button>
                    <span>› {user.management_group || '기관 미지정'}</span>
                </div>

                <div className="sds-page-header">
                    <div>
                        <h1 className="sds-page-header__title">
                            {user.name}
                            <small>{identity} · {user.management_group}</small>
                            {user.has_smart_ring && <span className="sds-badge sds-badge--info">스마트링</span>}
                        </h1>
                    </div>
                    <div className="sds-page-header__actions">
                        {/* 이미 화면에 없던 기능이다 — 눈에 보이지 않는 채로 그대로 둔다.
                            showChat / handleToggleChat 은 아래 TalkJS 모달과 짝이다. */}
                        <button
                            hidden="true"
                            className="umd-memo-table-view-btn"
                            onClick={handleToggleChat}
                            style={{
                                display: 'none',
                                background: showChat ? 'linear-gradient(135deg, #f44336, #d32f2f)' : 'linear-gradient(135deg, #1976ff, #0056cc)',
                                color: '#fff',
                                border: 'none',
                                fontWeight: 'bold'
                            }}
                        >
                            {showChat ? '❌ 채팅 닫기' : '💬 1:1 채팅'}
                        </button>
                        <button className="sds-btn sds-btn--secondary" onClick={handleOpenMedicalHistory}>
                            <Stethoscope size={16} aria-hidden="true" />병력 및 약물
                        </button>
                        <button className="sds-btn sds-btn--secondary" onClick={handleOpenMemoTable}>
                            <NotebookPen size={16} aria-hidden="true" />메모
                        </button>
                        <button className="sds-btn sds-btn--secondary" onClick={() => setShowVideoModal(true)}>
                            <Video size={16} aria-hidden="true" />교육영상
                        </button>
                        <button className="sds-btn sds-btn--primary" onClick={handleOpenSendModal}>
                            <Send size={16} aria-hidden="true" />문자 전송
                        </button>
                    </div>
                </div>

                <PatientTabs appUserId={id} active={detailTab} hasSmartRing={user.has_smart_ring} />

                {detailTab === 'records' ? (
                    <RecordsTab
                        activeMetric={activeTab}
                        onMetricChange={setActiveTab}
                        chartData={chartData}
                        allHealthData={allHealthData}
                    />
                ) : detailTab === 'frail' ? (
                    // 노쇠지수 탭(2026-10-16 사용자: 개요에 넣지 말고 탭으로). 설문은 설문 탭, 점수·비교·AI 브리핑·권장 운동은 여기.
                    <FrailIndexSection
                        index={frail.data?.index || null}
                        previousIndex={frail.data?.previousIndex || null}
                        assessment={frail.data?.assessment || null}
                        grades={frail.data?.grades || []}
                        loading={frail.loading}
                        error={frail.error}
                        briefing={frailBriefing.briefing}
                        briefingStatus={frailBriefing.status}
                        briefingError={frailBriefing.error}
                        briefingCreatedAt={frailBriefing.createdAt}
                        briefingAvailable={frail.data?.briefingAvailable === true}
                        onRegenerateBriefing={frailBriefing.regenerate}
                        guide={frailGuide.guide}
                        guideStatus={frailGuide.status}
                        guideError={frailGuide.error}
                        guideCreatedAt={frailGuide.createdAt}
                        guideCached={frailGuide.cached}
                        guideAvailable={frail.data?.guideAvailable === true}
                        onRegenerateGuide={frailGuide.regenerate}
                        guideSent={frailGuide.sent}
                        onSendGuide={frailGuide.send}
                        onPreviewGuide={frailGuide.preview}
                        guideSending={frailGuide.sending}
                        guideSendResult={frailGuide.sendResult}
                    />
                ) : (
                    <>
                        {/* 옛 사용자 정보 표에 있다가 개편 때 대체 없이 사라진 값들을 되살린다.
                            대시보드 목록에도 전화번호 열이 없어서, 이 줄이 없으면 담당자가
                            대상자에게 전화하려 할 때 번호를 볼 곳이 화면 어디에도 없다.
                            생년월일은 헤더 신원 줄로 갔다 — 여기서는 식별용이고, 떠나온
                            대시보드 행에도 있어서 따로 무게를 줄 이유가 없다.
                            값은 전부 이미 user 에 있다 — 새로 계산하거나 부르지 않는다. */}
                        <div className="sds-list detail-page__profile">
                            <div className="sds-list__row">
                                <div className="sds-list__body"><b>전화번호</b></div>
                                <div className="sds-list__spacer" />
                                <div className="sds-list__value">
                                    {telHref
                                        ? <a className="sds-link" href={telHref}>{user.mobile}</a>
                                        : '기록 없음'}
                                </div>
                            </div>
                            <div className="sds-list__row">
                                <div className="sds-list__body"><b>측정 시작일시</b></div>
                                <div className="sds-list__spacer" />
                                <div className="sds-list__value">{measureStartText}</div>
                            </div>
                        </div>

                        {/* 이번 주 요약. 사실 줄만 둔다 — AI 요약은 아래 인사이트 절에 있어 겹친다(2026-09-23 사용자 요청). */}
                        <WeeklySummaryPlaceholder latestDate={latestDate} measuredDays={measuredDays} staleNote={staleNote} />

                        {metricCards.length === 0
                            ? <div className="sds-notice">아직 측정 기록이 없습니다.</div>
                            : (
                                <div className="sds-metric-grid">
                                    {metricCards.map((card) => (
                                        <div key={card.key} className="sds-metric-card">
                                            <div className="sds-metric-card__head">
                                                {card.title}
                                                {card.stale
                                                    ? <span className="sds-badge sds-badge--none" title={`${card.measuredOn} 측정`}>오래된 값 · {shortDate(card.measuredOn)}</span>
                                                    : <RiskBadge level={card.level} />}
                                            </div>
                                            {/* 힌트와 추이는 별도 계획이 채운다. 빈 자리를 미리 잡아 두지 않는다 —
                                                채워질 때 한 번 늘어나는 편이 지금 빈 구멍으로 보이는 것보다 낫다. */}
                                            <div className={`sds-metric-card__value${card.stale ? ' sds-metric-card__value--stale' : ''}`}>
                                                {card.value}
                                                {card.unit && <span className="sds-metric-card__unit">{card.unit}</span>}
                                            </div>
                                            <div className="sds-metric-card__delta">{card.delta}</div>
                                        </div>
                                    ))}
                                </div>
                            )}

                        {/* 지난주 인사이트. 설계는 docs/superpowers/specs/2026-09-15-weekly-insight-push.md */}
                        <WeeklyInsightSection weeks={weeklyInsights.weeks} loading={weeklyInsights.loading} error={weeklyInsights.error} />

                        <MemoSection
                            currentDate={currentDate}
                            onPrevMonth={handlePrevMonth}
                            onNextMonth={handleNextMonth}
                            memos={memos}
                            selectedDate={selectedDate}
                            setSelectedDate={setSelectedDate}
                            memo={memo}
                            setMemo={setMemo}
                            memoCategory={memoCategory}
                            setMemoCategory={setMemoCategory}
                            MEMO_CATEGORIES={MEMO_CATEGORIES}
                            isNewMemo={isNewMemo}
                            setIsNewMemo={setIsNewMemo}
                            showMemoModal={showMemoModal}
                            setShowMemoModal={setShowMemoModal}
                            handleSaveMemo={handleSaveMemo}
                            handleDeleteMemo={handleDeleteMemo}
                            onOpenMemoTable={handleOpenMemoTableForDate}
                        />
                    </>
                )}
            </div>

            {showSendModal && (
                <div className="dashboard-detail-send-modal">
                    <div className="dashboard-detail-send-modal-content">
                        <div className="dashboard-detail-modal-header">
                            <h2 className="dashboard-detail-modal-title">문자 메시지 발송</h2>
                            <button className="dashboard-detail-close-button" onClick={() => setShowSendModal(false)}>×</button>
                        </div>
                        <div className="dashboard-detail-form-group" style={{ marginBottom: '16px' }}>
                            <label style={{ fontSize: '14px', fontWeight: '700', color: '#333', display: 'block', marginBottom: '8px', textAlign: 'left' }}>
                                메시지 제목 :
                                <input
                                    type="text"
                                    className="dashboard-detail-form-input"
                                    placeholder="제목을 입력해주세요"
                                    value={smsTitle}
                                    onChange={e => setSmsTitle(e.target.value)}
                                    style={{ width: '85%', padding: '10px 12px', border: '1px solid #e1e1e1', borderRadius: '4px', fontSize: '14px', color: '#333', backgroundColor: '#fff', height: '38px', boxSizing: 'border-box', marginTop: '8px', marginLeft: '8px' }}
                                />
                            </label>
                        </div>
                        <div className="dashboard-detail-form-group" style={{ marginBottom: '16px' }}>
                            <label style={{ fontSize: '14px', fontWeight: '700', color: '#333', display: 'block', marginBottom: '8px', textAlign: 'left' }}>
                                메시지 구분 :
                                <select
                                    className="dashboard-detail-form-input"
                                    value={smsType}
                                    onChange={e => setSmsType(e.target.value)}
                                    style={{ width: '85%', padding: '10px 12px', border: '1px solid #e1e1e1', borderRadius: '4px', fontSize: '14px', color: '#333', backgroundColor: '#fff', height: '38px', boxSizing: 'border-box', marginTop: '8px', marginLeft: '8px', appearance: 'none' }}
                                >
                                    <option value="공지">공지</option>
                                    <option value="알림">알림</option>
                                    <option value="경고">경고</option>
                                </select>
                            </label>
                        </div>
                        <div className="dashboard-detail-form-group" style={{ marginBottom: '16px' }}>
                            <label style={{ fontSize: '14px', fontWeight: '700', color: '#333', display: 'flex', marginBottom: '8px', textAlign: 'left', alignItems: 'flex-start' }}>
                                메시지 내용 :
                                <textarea
                                    className="dashboard-detail-form-textarea"
                                    placeholder="메시지 내용을 입력해주세요"
                                    value={smsContent}
                                    onChange={e => setSmsContent(e.target.value)}
                                    style={{ width: '85%', padding: '10px 12px', border: '1px solid #e1e1e1', borderRadius: '4px', fontSize: '14px', color: '#333', backgroundColor: '#fff', minHeight: '120px', boxSizing: 'border-box', marginLeft: '8px', resize: 'vertical' }}
                                />
                            </label>
                        </div>
                        {(hasGuardianCarePlan || carePlanShareUrl) && (
                        <div style={{ marginBottom: '16px', marginLeft: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {hasGuardianCarePlan && (
                                    <button
                                        type="button"
                                        onClick={insertCarePlanLink}
                                        disabled={isLoading}
                                        style={{ padding: '8px 20px', border: '1px solid #e0e0e0', borderRadius: '4px', background: '#fff', fontSize: '15px', color: '#222', cursor: 'pointer' }}
                                    >
                                        케어플랜 링크 넣기
                                    </button>
                                )}
                                {carePlanShareUrl && (
                                    <button
                                        type="button"
                                        onClick={revokeCarePlanLink}
                                        disabled={isLoading}
                                        style={{ padding: '8px 20px', border: '1px solid #e1c1c1', borderRadius: '4px', background: '#fff', fontSize: '15px', color: '#b45309', cursor: 'pointer' }}
                                    >
                                        링크 폐기
                                    </button>
                                )}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px' }}>
                                요약본 전문 대신 링크가 들어갑니다
                            </div>
                        </div>
                        )}
                        {/* 이 대상자에게 보낸 문자 이력. 무엇을 이미 보냈는지 모르고
                            다시 쓰는 일을 줄인다. */}
                        <div className="sms-history">
                            <div className="sms-history-head">보낸 문자</div>
                            {smsHistoryError ? (
                                <div className="sms-history-empty">{smsHistoryError}</div>
                            ) : smsHistory === null ? (
                                <div className="sms-history-empty">불러오는 중…</div>
                            ) : smsHistory.length === 0 ? (
                                <div className="sms-history-empty">
                                    보낸 문자가 없습니다.
                                    <span className="sms-history-note">
                                        2026-08-12 이전 발송분은 대상자와 연결돼 있지 않아 표시되지 않습니다.
                                    </span>
                                </div>
                            ) : (
                                <ul className="sms-history-list">
                                    {smsHistory.map((entry) => (
                                        <li key={entry.id} className="sms-history-item">
                                            <div className="sms-history-meta">
                                                <span className="sms-history-date">
                                                    {entry.sent_date || entry.scheduled_date || '-'}
                                                </span>
                                                <span className="sms-history-type">{entry.message_type}</span>
                                            </div>
                                            <div className="sms-history-title">{entry.title}</div>
                                            <div className="sms-history-content">{entry.content}</div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="dashboard-detail-button-container">
                            <button
                                className="dashboard-detail-cancel-button"
                                onClick={() => setShowSendModal(false)}
                            >
                                취소
                            </button>
                            <button
                                className="dashboard-detail-send-button"
                                onClick={handleSendSMS}
                                disabled={isLoading || !smsTitle.trim() || !smsContent.trim()}
                            >
                                {isLoading ? '전송 중...' : '발송'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* TalkJS 다이얼로그 모달 채팅창 (React Component 방식) */}
            {showChat && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ width: '600px', height: '700px', maxWidth: '90%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: '0', overflow: 'hidden' }}>
                        <div className="modal-header" style={{ padding: '16px 20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: '0', fontSize: '18px', fontWeight: 'bold' }}>💬 {user?.name || '-'} 님과의 대화</h3>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <button
                                    onClick={() => sendChatUrlSMS(false)}
                                    style={{
                                        background: 'linear-gradient(135deg, #28a745, #218838)',
                                        color: '#fff',
                                        border: 'none',
                                        padding: '6px 12px',
                                        borderRadius: '4px',
                                        fontSize: '13px',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        transition: 'all 0.2s',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.filter = 'brightness(1.1)'}
                                    onMouseOut={(e) => e.currentTarget.style.filter = 'none'}
                                >
                                    <span>✉</span> 채팅 URL 문자 전송
                                </button>
                                <button onClick={() => setShowChat(false)} style={{ background: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666', lineHeight: '1' }}>×</button>
                            </div>
                        </div>
                        <div style={{ flex: '1', width: '100%', height: 'calc(100% - 60px)' }}>
                            <Session 
                                appId={process.env.REACT_APP_TALKJS_APP_ID || 'tR4HG3a1'} 
                                syncUser={syncUser}
                            >
                                <Chatbox
                                    syncConversation={syncConversation}
                                    style={{ width: '100%', height: '100%' }}
                                />
                            </Session>
                        </div>
                    </div>
                </div>
            )}

            {showMemoTableModal && (
                <div className="modal-overlay">
                    <div className="modal-content umd-memo-table-modal">
                        <div className="modal-header">
                            <h3>월별 메모 내역</h3>
                            <button onClick={() => setShowMemoTableModal(false)} className="umd-memo-table-close-btn" style={{ marginLeft: 'auto' }}>닫기</button>
                        </div>
                        <div className="umd-memo-table-header">
                            <div className="umd-memo-table-header-left">
                                <button onClick={handleMemoTablePrevMonth} className="umd-memo-table-nav-btn">◀</button>
                                <span className="umd-memo-table-month">{memoTableMonth.getFullYear()}년 {memoTableMonth.getMonth() + 1}월</span>
                                <button onClick={handleMemoTableNextMonth} className="umd-memo-table-nav-btn">▶</button>
                            </div>
                        </div>
                        <table className="umd-memo-table">
                            <thead>
                                <tr>
                                    <th>작성일자</th>
                                    <th>구분</th>
                                    <th>내용</th>
                                    <th>작업</th>
                                </tr>
                            </thead>
                            <tbody>
                                {memoTableLoading ? (
                                    <tr><td colSpan="4" className="umd-memo-table-loading">로딩 중...</td></tr>
                                ) : filteredMemoTableData.length === 0 ? (
                                    <tr><td colSpan="4" className="umd-memo-table-empty">메모가 없습니다.</td></tr>
                                ) : (
                                    filteredMemoTableData.map((row, idx) => (
                                        <tr
                                            key={idx}
                                            className={getDateOnly(row.memo_data) === getDateOnly(selectedDateForTable) ? "highlighted-memo-row" : ""}
                                        >
                                            <td>{row.memo_data}</td>
                                            <td>{editRowIdx === idx ? (
                                                <select value={editCategory} onChange={e => setEditCategory(e.target.value)} className="umd-category-select">
                                                    {MEMO_CATEGORIES.map((category) => (
                                                        <option key={category} value={category}>{category}</option>
                                                    ))}
                                                </select>
                                            ) : row.category}</td>
                                            <td>{editRowIdx === idx ? (
                                                <input type="text" value={editMemo} onChange={e => setEditMemo(e.target.value)} className="umd-memo-input" />
                                            ) : row.content}</td>
                                            <td>
                                                {editRowIdx === idx ? (
                                                    <>
                                                        <button className="umd-memo-table-save-btn" onClick={() => handleUpdateMemo(row, idx)}>저장</button>
                                                        <button className="umd-memo-table-delete-btn" onClick={() => setEditRowIdx(null)}>취소</button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button className="umd-memo-table-save-btn" onClick={() => {
                                                            setEditRowIdx(idx);
                                                            setEditMemo(row.content);
                                                            setEditCategory(MEMO_CATEGORIES.includes(row.category));
                                                            setEditDate(row.memo_data);
                                                        }}>수정</button>
                                                        <button className="umd-memo-table-delete-btn" onClick={() => handleDeleteMemoRow(row)}>삭제</button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                                <tr>
                                    <td>
                                        <select
                                            value={selectedDate}
                                            onChange={e => setSelectedDate(e.target.value)}
                                            className="umd-date-select"
                                        >
                                            {Array.from({ length: 31 }, (_, i) => {
                                                const day = String(i + 1).padStart(2, '0');
                                                const month = String(memoTableMonth.getMonth() + 1).padStart(2, '0');
                                                const year = memoTableMonth.getFullYear();
                                                const value = `${year}-${month}-${day}`;
                                                return (
                                                    <option key={i + 1} value={value}>
                                                        {year}년 {memoTableMonth.getMonth() + 1}월 {i + 1}일
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </td>
                                    <td>
                                        <select
                                            value={memoCategory}
                                            onChange={e => setMemoCategory(e.target.value)}
                                            className="umd-category-select"
                                        >
                                            {MEMO_CATEGORIES.map((category) => (
                                                <option key={category} value={category}>{category}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td>
                                        <input
                                            type="text"
                                            value={memo}
                                            onChange={e => setMemo(e.target.value)}
                                            placeholder="내용을 입력하세요"
                                            className="umd-memo-input"
                                        />
                                    </td>
                                    <td>
                                        <button onClick={handleSaveMemo} className="umd-memo-table-save-btn">저장</button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <EducationalVideoDialog
                isOpen={showVideoModal} 
                onClose={() => setShowVideoModal(false)} 
                user={user} 
            />

            <MedicalHistoryModal
                isOpen={showMedicalHistoryModal}
                onClose={() => setShowMedicalHistoryModal(false)}
                medicalHistory={medicalHistory}
                setMedicalHistory={setMedicalHistory}
                medication={medication}
                setMedication={setMedication}
                emrSummary={emrSummary}
                onSave={handleSaveMedicalHistory}
            />
        </div>
    );
}

/**
 * 이번 주 요약 자리. AI 문장은 별도 계획이 채운다.
 * 그때까지도 담당자가 볼 것이 있어야 하므로, 지금 데이터로 확실히 말할 수 있는
 * 사실 한 줄(마지막 측정이 언제였는지)을 둔다.
 */
const WeeklySummaryPlaceholder = ({ latestDate, measuredDays, staleNote = null }) => (
    <div className="sds-alert-row detail-page__summary">
        <span className="sds-alert-row__icon detail-page__summary-icon">
            <Activity size={20} aria-hidden="true" />
        </span>
        <div className="sds-alert-row__body">
            <b>{latestDate ? `마지막 측정 ${latestDate}` : '최근 측정 기록이 없습니다'}</b>
            <small>{measuredDays != null ? `최근 7일 중 ${measuredDays}일 측정했어요.` : '측정을 시작하면 여기에 요약이 나옵니다.'}</small>
            {staleNote && <small className="detail-page__summary-stale">{staleNote}</small>}
        </div>
    </div>
);

